import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import teacherAssignmentsService from '../../services/teacherAssignmentsService';
import DialogModal from '../../components/DialogModal';
import { API_URL } from '../../utils/api';
import { Plus, Trash, X, VideoCamera, ClipboardText, UploadSimple } from '@phosphor-icons/react';

const emptyModuleForm = { title: '', description: '', class_id: '', subject_id: '', academic_term_id: '', order_index: 0 };
const emptyItemForm = { title: '', video_url: '', assignment_id: '' };
const MAX_VIDEO_UPLOAD_MB = 200;

// Hosted (uploaded) videos are stored as a relative /uploads/... path served
// by the backend, not the frontend dev server — needs the backend origin
// prefixed. External links (video_source 'link') are already absolute URLs.
const resolveVideoUrl = (item) => (item.video_source === 'upload' && item.video_url ? `${API_URL}${item.video_url}` : item.video_url);

function TeacherModulesSection({ taughtClasses = [] }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [moduleForm, setModuleForm] = useState(emptyModuleForm);
  const [submitting, setSubmitting] = useState(false);
  const [itemModalModule, setItemModalModule] = useState(null);
  const [itemType, setItemType] = useState('video'); // 'video' | 'upload' | 'quiz'
  const [itemForm, setItemForm] = useState(emptyItemForm);
  const [videoFile, setVideoFile] = useState(null);
  const [availableAssignments, setAvailableAssignments] = useState([]);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'module'|'item', id }
  const [deleteLoading, setDeleteLoading] = useState(false);

  const loadModules = useCallback(() => {
    setLoading(true);
    teacherAssignmentsService.listCourseModules()
      .then(data => setModules(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load course modules'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);

  const openCreateModal = () => {
    setModuleForm(emptyModuleForm);
    setShowModuleModal(true);
  };

  const handleClassSubjectChange = (assignmentKey) => {
    const cls = taughtClasses.find(c => `${c.class_id}::${c.subject_id}` === assignmentKey);
    if (!cls) {
      setModuleForm(f => ({ ...f, class_id: '', subject_id: '', academic_term_id: '' }));
      return;
    }
    setModuleForm(f => ({ ...f, class_id: cls.class_id, subject_id: cls.subject_id, academic_term_id: cls.academic_term_id }));
  };

  const handleCreateModule = async (e) => {
    e.preventDefault();
    if (!moduleForm.title || !moduleForm.class_id || !moduleForm.subject_id) {
      toast.error('Title and class/subject are required');
      return;
    }
    setSubmitting(true);
    try {
      await teacherAssignmentsService.createCourseModule(moduleForm);
      toast.success('Course module created');
      setShowModuleModal(false);
      loadModules();
    } catch (error) {
      toast.error(error.message || 'Failed to create module');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePublish = async (module) => {
    try {
      await teacherAssignmentsService.updateCourseModule(module.id, { is_published: !module.is_published });
      toast.success(module.is_published ? 'Module unpublished' : 'Module published — students can now see it');
      loadModules();
    } catch (error) {
      toast.error(error.message || 'Failed to update module');
    }
  };

  const openItemModal = (module) => {
    setItemForm(emptyItemForm);
    setItemType('video');
    setVideoFile(null);
    setAvailableAssignments([]);
    setItemModalModule(module);
  };

  const handleItemTypeChange = (type) => {
    setItemType(type);
    setItemForm(emptyItemForm);
    setVideoFile(null);
    if (type === 'quiz' && itemModalModule) {
      setLoadingAssignments(true);
      teacherAssignmentsService.listMyAssignments({ class_id: itemModalModule.class_id, subject_id: itemModalModule.subject_id })
        .then(data => setAvailableAssignments(data?.items || []))
        .catch(() => toast.error('Failed to load your assignments'))
        .finally(() => setLoadingAssignments(false));
    }
  };

  const handleAssignmentPick = (assignmentId) => {
    const assignment = availableAssignments.find(a => a.assignment_id === assignmentId || a.id === assignmentId);
    setItemForm({
      title: assignment ? assignment.title : '',
      video_url: '',
      assignment_id: assignmentId,
    });
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (itemType === 'video' && (!itemForm.title || !itemForm.video_url)) {
      toast.error('Title and video link are required');
      return;
    }
    if (itemType === 'upload' && (!itemForm.title || !videoFile)) {
      toast.error('Title and a video file are required');
      return;
    }
    if (itemType === 'upload' && videoFile && videoFile.size > MAX_VIDEO_UPLOAD_MB * 1024 * 1024) {
      toast.error(`Video exceeds the ${MAX_VIDEO_UPLOAD_MB} MB limit`);
      return;
    }
    if (itemType === 'quiz' && !itemForm.assignment_id) {
      toast.error('Select an assignment to attach');
      return;
    }
    setSubmitting(true);
    try {
      if (itemType === 'upload') {
        const formData = new FormData();
        formData.append('title', itemForm.title);
        formData.append('file', videoFile);
        await teacherAssignmentsService.uploadModuleVideo(itemModalModule.id, formData);
      } else {
        const payload = itemType === 'video'
          ? { title: itemForm.title, video_url: itemForm.video_url }
          : { title: itemForm.title, assignment_id: itemForm.assignment_id };
        await teacherAssignmentsService.addModuleItem(itemModalModule.id, payload);
      }
      toast.success('Item added');
      setItemModalModule(null);
      loadModules();
    } catch (error) {
      toast.error(error.message || 'Failed to add item');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      if (deleteTarget.type === 'module') {
        await teacherAssignmentsService.deleteCourseModule(deleteTarget.id);
        toast.success('Module deleted');
      } else {
        await teacherAssignmentsService.deleteModuleItem(deleteTarget.id);
        toast.success('Item deleted');
      }
      setDeleteTarget(null);
      loadModules();
    } catch (error) {
      toast.error(error.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading course modules...</div>;
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
          Group video links and quizzes into ordered modules per class/subject. Publish a module to make it visible to students.
        </p>
        <button className="btn btn-primary" type="button" onClick={openCreateModal} disabled={taughtClasses.length === 0}>
          <Plus size={16} /> New Module
        </button>
      </div>

      {modules.length === 0 && (
        <div className="empty-state-card">
          <p>No course modules yet</p>
        </div>
      )}

      {modules.map(module => (
        <div key={module.id} className="form-card" style={{ padding: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: 0 }}>{module.title}</h3>
              {module.description && <p style={{ margin: '4px 0', color: '#666', fontSize: 14 }}>{module.description}</p>}
              <span className={`badge ${module.is_published ? 'active' : 'inactive'}`}>
                {module.is_published ? 'Published' : 'Draft'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-sm" type="button" onClick={() => togglePublish(module)}>
                {module.is_published ? 'Unpublish' : 'Publish'}
              </button>
              <button className="btn btn-sm" type="button" onClick={() => openItemModal(module)}>
                <Plus size={14} /> Item
              </button>
              <button className="btn-icon btn-danger" type="button" onClick={() => setDeleteTarget({ type: 'module', id: module.id })} title="Delete module">
                <Trash size={16} />
              </button>
            </div>
          </div>

          {(module.items || []).length > 0 && (
            <ul style={{ marginTop: 12, paddingLeft: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
              {module.items.map(item => (
                <li key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f7f7f7', borderRadius: 6, flexWrap: 'wrap', gap: 6 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {item.assignment_id ? <ClipboardText size={16} /> : <VideoCamera size={16} />}
                    {item.video_url ? (
                      <a href={resolveVideoUrl(item)} target="_blank" rel="noopener noreferrer">{item.title}</a>
                    ) : (
                      item.title
                    )}
                    {item.video_source === 'upload' && <span className="badge active" style={{ fontSize: 11 }}>Hosted</span>}
                    {item.assignment_id && <span className="badge inactive" style={{ fontSize: 11 }}>Quiz</span>}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12, color: '#666' }} title={`${item.completed_count} of ${item.total_students} students`}>
                      {item.total_students > 0
                        ? `${item.completion_percentage}% of class (${item.completed_count}/${item.total_students})`
                        : 'No students in class yet'}
                    </span>
                    <button className="btn-icon btn-danger" type="button" onClick={() => setDeleteTarget({ type: 'item', id: item.id })} title="Delete item">
                      <Trash size={14} />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}

      {showModuleModal && (
        <div className="modal-overlay" onClick={() => setShowModuleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Course Module</h2>
              <button className="modal-close" onClick={() => setShowModuleModal(false)}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateModule} className="modal-form">
              <div className="form-group">
                <label>Class / Subject</label>
                <select
                  value={moduleForm.class_id && moduleForm.subject_id ? `${moduleForm.class_id}::${moduleForm.subject_id}` : ''}
                  onChange={(e) => handleClassSubjectChange(e.target.value)}
                  disabled={submitting}
                  required
                >
                  <option value="">Select a class/subject you teach</option>
                  {taughtClasses.map(c => (
                    <option key={`${c.class_id}::${c.subject_id}`} value={`${c.class_id}::${c.subject_id}`}>
                      {c.class_name} — {c.subject_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={moduleForm.title}
                  onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                  placeholder="e.g., Week 1 — Introduction to Fractions"
                  disabled={submitting}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={moduleForm.description}
                  onChange={(e) => setModuleForm({ ...moduleForm, description: e.target.value })}
                  disabled={submitting}
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModuleModal(false)} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Module'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {itemModalModule && (
        <div className="modal-overlay" onClick={() => setItemModalModule(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Item</h2>
              <button className="modal-close" onClick={() => setItemModalModule(null)}><X size={20} /></button>
            </div>
            <form onSubmit={handleAddItem} className="modal-form">
              <div className="form-group">
                <label>Item Type</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className={itemType === 'video' ? 'btn btn-primary' : 'btn btn-secondary'}
                    onClick={() => handleItemTypeChange('video')}
                    disabled={submitting}
                  >
                    <VideoCamera size={14} /> Video Link
                  </button>
                  <button
                    type="button"
                    className={itemType === 'upload' ? 'btn btn-primary' : 'btn btn-secondary'}
                    onClick={() => handleItemTypeChange('upload')}
                    disabled={submitting}
                  >
                    <UploadSimple size={14} /> Upload Video
                  </button>
                  <button
                    type="button"
                    className={itemType === 'quiz' ? 'btn btn-primary' : 'btn btn-secondary'}
                    onClick={() => handleItemTypeChange('quiz')}
                    disabled={submitting}
                  >
                    <ClipboardText size={14} /> Quiz / Assignment
                  </button>
                </div>
              </div>

              {itemType === 'video' && (
                <>
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={itemForm.title}
                      onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                      placeholder="e.g., Lesson video: Adding Fractions"
                      disabled={submitting}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Video Link</label>
                    <input
                      type="url"
                      value={itemForm.video_url}
                      onChange={(e) => setItemForm({ ...itemForm, video_url: e.target.value })}
                      placeholder="https://..."
                      disabled={submitting}
                      required
                    />
                    <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      YouTube, Vimeo, or any external link — opens in a new tab, not embedded.
                    </p>
                  </div>
                </>
              )}

              {itemType === 'upload' && (
                <>
                  <div className="form-group">
                    <label>Title</label>
                    <input
                      type="text"
                      value={itemForm.title}
                      onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                      placeholder="e.g., Lesson video: Adding Fractions"
                      disabled={submitting}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Video File</label>
                    <input
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,video/x-msvideo"
                      onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                      disabled={submitting}
                      required
                    />
                    <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      MP4, WebM, MOV, or AVI, up to {MAX_VIDEO_UPLOAD_MB} MB. Hosted on this server and played inline
                      for students — no external platform needed.
                    </p>
                  </div>
                </>
              )}

              {itemType === 'quiz' && (
                <>
                  <div className="form-group">
                    <label>Assignment</label>
                    <select
                      value={itemForm.assignment_id}
                      onChange={(e) => handleAssignmentPick(e.target.value)}
                      disabled={submitting || loadingAssignments}
                      required
                    >
                      <option value="">
                        {loadingAssignments ? 'Loading your assignments...' : 'Select an assignment'}
                      </option>
                      {availableAssignments.map(a => (
                        <option key={a.id || a.assignment_id} value={a.id || a.assignment_id}>
                          {a.title} ({a.assignment_type}, {a.status})
                        </option>
                      ))}
                      {!loadingAssignments && availableAssignments.length === 0 && (
                        <option disabled>No assignments for this class/subject yet</option>
                      )}
                    </select>
                    <p style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                      Create the assignment first under Assignments, then attach it here. A student's completion is
                      based on their submission, not a manual checkbox.
                    </p>
                  </div>
                  <div className="form-group">
                    <label>Display Title (optional override)</label>
                    <input
                      type="text"
                      value={itemForm.title}
                      onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })}
                      disabled={submitting}
                    />
                  </div>
                </>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setItemModalModule(null)} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Adding...' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={deleteTarget?.type === 'module' ? 'Delete Module' : 'Delete Item'}
        message={deleteTarget?.type === 'module' ? 'This will permanently delete this module and all its items. Continue?' : 'This will permanently delete this item. Continue?'}
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

export default TeacherModulesSection;
